import React, { useEffect, useState, useContext } from "react";
import { CheckCircle } from "@material-ui/icons";
import { useMutation } from "@apollo/react-hooks";
import { useParams, useHistory } from "react-router-dom";
import {
  Button,
  Box,
  Typography,
  makeStyles,
  Paper,
  Divider
} from "@material-ui/core";
import {
  IntroText,
  Loader,
  Sheet,
  SheetContent,
  LogoGGT,
  LogoGGV,
  PatientDetails,
  ServiceLabelWithIcon
} from "../../components";
import QRCode from "qrcode.react";
import { POST_LOOKUP_APPOINTMENT } from "../../queries";
import {
  CONSUMER_SESSION_AUTH,
  API_SERVICE_MAP,
  ROUTE_PATHS
} from "../../constants";
import { useTranslation } from "react-i18next";
import { BrandThemeContext } from "../../contexts";
import { isVaxService } from "../../utils";

const useClasses = makeStyles(theme => ({
  spanBlock: {
    display: "block"
  },
  iconSuccess: {
    color: theme.palette.success.main
  }
}));

const AppointmentCard = () => {
  const { t } = useTranslation();
  const classes = useClasses();
  const { appointment_id, dob } = useParams();
  const history = useHistory();
  const { themeName, setThemeName } = useContext(BrandThemeContext);

  const [appointment, setAppointment] = useState();

  /**
   * Check for appointment id and dob
   */
  useEffect(() => {
    if (!appointment_id) return null;
    if (!dob) history.push(`/appointment/${appointment_id}/auth`);
  }, [appointment_id, dob]);

  /**
   * Switch brand depending on service selection
   */
  useEffect(() => {
    if (!appointment) return;

    if (
      appointment.service_selection_codes &&
      isVaxService(appointment.service_selection_codes)
    ) {
      setThemeName("vax");
    } else {
      setThemeName("tested");
    }
  }, [appointment]);

  const [postLookupAppointment, { loading, error }] = useMutation(
    POST_LOOKUP_APPOINTMENT,
    {
      onCompleted: ({ appointment: theAppointment }) => {
        if (theAppointment) setAppointment(theAppointment);
      }
    }
  );

  /**
   * Lookup appointment on mount
   */
  useEffect(() => {
    postLookupAppointment({
      variables: {
        body: {
          appointment_id,
          dob
        }
      }
    });
  }, [postLookupAppointment, appointment_id, dob]);

  const handleScheduleTest = () => {
    history.push(ROUTE_PATHS.GGT.SCHEDULE_START);
  };

  if (loading || !appointment) return <Loader />;
  if (error)
    return (
      <Typography color="error">{t("appointment.card.error_api")}</Typography>
    );

  return (
    <Sheet data-cy="appointment-card-view">
      <Box textAlign="center" pb={2}>
        {themeName === "GGT" && <LogoGGT />}
        {themeName === "GGV" && <LogoGGV />}
      </Box>
      <SheetContent>
        <Box mb={2}>
          <Paper>
            <Box p={2}>
              {appointment && appointment.appointment_id ? (
                <>
                  <Box mb={1} textAlign="center">
                    <CheckCircle
                      className={classes.iconSuccess}
                      fontSize="large"
                    />
                  </Box>
                  <IntroText>{t("appointment.card.intro_text")}</IntroText>
                  {Array.isArray(appointment.service_selection_codes) && (
                    <Box mb={2}>
                      {appointment.service_selection_codes.map(service_code => (
                        <React.Fragment key={`service-${service_code}`}>
                          {API_SERVICE_MAP[service_code] ? (
                            <ServiceLabelWithIcon
                              icon={API_SERVICE_MAP[service_code].icon}
                              label={API_SERVICE_MAP[service_code].label}
                            />
                          ) : null}
                        </React.Fragment>
                      ))}
                    </Box>
                  )}
                  {appointment &&
                    (appointment.patient_name || appointment.patient_dob) && (
                      <PatientDetails
                        name={appointment.patient_name}
                        dob={appointment.patient_dob}
                      />
                    )}
                  {appointment && (appointment.date || appointment.location) && (
                    <IntroText>
                      {appointment.date && (
                        <Box
                          component="span"
                          className={classes.spanBlock}
                          data-cy="schedule-screen-confirmation-date"
                        >
                          <strong>{appointment.date}</strong>
                        </Box>
                      )}
                      {appointment.location && (
                        <Box
                          component="span"
                          className={classes.spanBlock}
                          data-cy="schedule-screen-confirmation-location"
                        >
                          <strong>{appointment.location}</strong>
                        </Box>
                      )}
                    </IntroText>
                  )}
                  <Box my={2} textAlign="center">
                    <QRCode value={appointment.appointment_id.toString()} />
                  </Box>
                  <Typography align="center" paragraph>
                    {appointment.appointment_id}
                  </Typography>
                  <Typography align="center" paragraph>
                    {t("appointment.card.qr_code_disclaimer")}
                  </Typography>
                  {!isVaxService(appointment.service_selection_codes) && (
                    <>
                      <Typography align="center" paragraph>
                        {t("appointment.card.symptoms_disclaimer")}
                      </Typography>
                      <Button
                        color="default"
                        size="large"
                        variant="contained"
                        fullWidth
                        href="https://www.gogettested.com/confirmationv1"
                        target="_blank"
                      >
                        {t("common.learn_more")}
                      </Button>
                      <Box my={2}>
                        <Divider variant="middle" />
                      </Box>
                      <Button
                        color="primary"
                        size="large"
                        variant="contained"
                        fullWidth
                        onClick={e => handleScheduleTest()}
                        data-cy="button-schedule-test"
                      >
                        {t("appointment.card.button_schedule_test")}
                      </Button>
                    </>
                  )}
                </>
              ) : (
                <Typography
                  data-cy="appointment-card-view-not-found"
                  color="error"
                  align="center"
                >
                  {t("appointment.card.error")}
                </Typography>
              )}
            </Box>
          </Paper>
          {appointment && (
            <Box p={2}>
              <Typography align="center" paragraph>
                {t("appointment.card.thank_you")}
              </Typography>
              <Typography align="center" paragraph>
                {t("appointment.card.id_disclaimer")}
              </Typography>
              <Typography align="center" paragraph>
                {t("appointment.card.sign_off")}
              </Typography>
            </Box>
          )}
        </Box>
      </SheetContent>
    </Sheet>
  );
};

export { AppointmentCard };
